import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
// @mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
// components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
// hooks
import { useAccount } from './hooks'; // 

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export default function ProfileInfoDialog({ open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  
  // Sử dụng getMyProfile từ hook để lấy thông tin user hiện tại
  const { updateMyProfile, getMyProfile, loading } = useAccount(); 

  const ProfileSchema = Yup.object().shape({
    tenTaiKhoan: Yup.string(),
    email: Yup.string().required('Email là bắt buộc').email('Email không hợp lệ'),
    sdt: Yup.string().nullable(),
  });

  const defaultValues = {
    tenTaiKhoan: '',
    email: '',
    sdt: '',
  };

  const methods = useForm({
    resolver: yupResolver(ProfileSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // Gọi API lấy thông tin user khi mở dialog
  useEffect(() => {
    if (open) {
      const fetchCurrentUser = async () => {
        try {
          const user = await getMyProfile(); // Gọi API getMyTaiKhoan
          if (user) {
            reset({
              tenTaiKhoan: user.tenTaiKhoan || '',
              email: user.email || '',
              sdt: user.sdt || '',
            });
          }
        } catch (error) {
          console.error('Lỗi tải thông tin cá nhân:', error);
          enqueueSnackbar('Không thể tải thông tin cá nhân', { variant: 'error' });
        }
      };

      fetchCurrentUser();
    }
  }, [open, getMyProfile, reset, enqueueSnackbar]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateMyProfile({
        email: data.email,
        sdt: data.sdt,
      });
      enqueueSnackbar('Cập nhật thông tin thành công!', { variant: 'success' });
      onClose();
      window.location.reload(); 
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(error.message || 'Có lỗi xảy ra', { variant: 'error' });
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cập nhật thông tin cá nhân</DialogTitle>

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          {loading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
               <CircularProgress />
             </Box>
          ) : (
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Box
                rowGap={3}
                columnGap={2}
                display="grid"
                gridTemplateColumns={{
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                }}
              >
                  <RHFTextField name="tenTaiKhoan" label="Tên đăng nhập" disabled />
                  <RHFTextField name="sdt" label="Số điện thoại" />
              </Box>
              
              <RHFTextField name="email" label="Email" />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Hủy
          </Button>
          <LoadingButton 
            type="submit" 
            variant="contained" 
            loading={isSubmitting}
            disabled={loading} // Disable nút lưu khi đang tải dữ liệu
          >
            Lưu thay đổi
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}